import { AlertTriangle, RefreshCw } from 'lucide-react';

import { cn } from '@/lib/cn';

interface Props {
  title?: string;
  message?: string;
  onRetry?: () => void;
  className?: string;
}

/**
 * Async-error surface used by every `useQuery` site when `isError`.
 * BUILD_STANDARDS §5: never render a dead spinner — either loading,
 * empty, or this. Ships human copy, not raw error codes.
 */
export function ErrorState({
  title = 'Something went wrong',
  message = "We couldn't load this. Please try again in a moment.",
  onRetry,
  className,
}: Props): JSX.Element {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-mb-border bg-mb-surface px-6 py-10 text-center',
        className,
      )}
    >
      <div className="rounded-full bg-mb-danger-soft p-2 text-mb-danger">
        <AlertTriangle className="h-5 w-5" aria-hidden />
      </div>
      <div>
        <div className="font-medium text-mb-text">{title}</div>
        <div className="mt-1 text-sm text-mb-text-muted">{message}</div>
      </div>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mb-btn-secondary"
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
          Try again
        </button>
      ) : null}
    </div>
  );
}
